import { useState } from "react";
import { clearSavedLogin } from "@/lib/savedLogin";
import { useNavigate } from "react-router-dom";
import { Loader2, KeyRound, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { TulsiLogo } from "@/lib/brand";

const RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

export default function ChangePassword() {
  const { user, logout, markPasswordChanged, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const validate = () => {
    if (!RULE.test(next)) return "New password must be at least 8 chars with letters and digits.";
    if (next !== confirm) return "New password and confirmation do not match.";
    if (next === current) return "New password must be different from current.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) return setError(v);
    setBusy(true);
    try {
      await api.post(
        "/auth/change-password",
        { current_password: current, new_password: next },
        { skipAuthRedirect: true }
      );
      markPasswordChanged();
      clearSavedLogin(); // the saved password is now out of date
      await refreshMe();
      toast.success("Password updated");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div
      data-testid="change-password-page"
      className="min-h-screen w-full flex items-center justify-center px-4 py-10"
      style={{
        backgroundImage:
          "radial-gradient(1200px 600px at 90% -10%, rgba(229,25,127,0.14), transparent), radial-gradient(1000px 600px at 10% 110%, rgba(109,44,145,0.14), transparent)",
      }}
    >
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <TulsiLogo size={56} />
        </div>
        <div className="bg-white border border-neutral-200 shadow-xl shadow-[#6D2C91]/5 rounded-2xl p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-5">
            <div className="h-10 w-10 rounded-xl tulsi-gradient text-white flex items-center justify-center shrink-0">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-neutral-900">
                {user?.must_change_password ? "Set a new password" : "Change password"}
              </h1>
              <p className="text-sm text-neutral-500 mt-0.5">
                {user?.must_change_password
                  ? "This is your first login. Please choose a new password to continue."
                  : "Update the password on your account."}
              </p>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-neutral-700">Current password</Label>
              <Input
                data-testid="cp-current-input"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                className="mt-1 h-11"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-neutral-700">New password</Label>
              <Input
                data-testid="cp-new-input"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                className="mt-1 h-11"
              />
              <p className="text-[11px] text-neutral-500 mt-1">
                Min 8 characters, must include at least one letter and one digit.
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-neutral-700">Confirm new password</Label>
              <Input
                data-testid="cp-confirm-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="mt-1 h-11"
              />
            </div>

            {error && (
              <div
                data-testid="cp-error"
                className="text-sm rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="cp-submit-button"
              disabled={busy || !current || !next || !confirm}
              className="w-full h-11 tulsi-gradient text-white hover:opacity-95 font-semibold rounded-xl shadow-md shadow-[#E5197F]/20 disabled:opacity-50"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
                </>
              ) : (
                "Update password"
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-neutral-100 flex items-center justify-between">
            <span className="text-xs text-neutral-500">
              Signed in as{" "}
              <span className="font-medium text-neutral-700">
                {user?.email || user?.phone}
              </span>
            </span>
            <button
              type="button"
              onClick={onLogout}
              data-testid="cp-logout-button"
              className="text-xs font-medium text-neutral-500 hover:text-[#E5197F] inline-flex items-center gap-1"
            >
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
